import { useEffect, useMemo, useState } from "react";
import { Download, Plus, Printer, RefreshCw, Save, Search } from "lucide-react";
import { deliveryApi as api } from "../../api/deliveryApi.js";
import { exportCsv, exportElementExcel } from "../../api.js";

const blankVendor = {
  vendorName: "",
  address: "",
  qcName: "",
  stitchingItems: [],
  active: true,
};
const badge = (value) => (
  <span
    className={`status-badge ${value === "COMPLETED" ? "success" : value === "PENDING" ? "warning" : ""}`}
  >
    {value}
  </span>
);
export default function DeliveryDepartmentPage({
  mode = "vendors",
  notify,
  onPageChange,
}) {
  if (mode === "vendors") return <Vendors notify={notify} />;
  if (mode === "plans")
    return <Plans notify={notify} onPageChange={onPageChange} />;
  if (mode === "section") return <SectionPlan notify={notify} />;
  return <History notify={notify} />;
}
function Head({ title, sub, actions }) {
  return (
    <div className="classic-title">
      <div>
        <small>DELIVERY DEPARTMENT</small>
        <h2>{title}</h2>
        <p>{sub}</p>
      </div>
      <div className="page-actions">{actions}</div>
    </div>
  );
}
function Vendors({ notify }) {
  const [rows, setRows] = useState([]),
    [form, setForm] = useState(blankVendor),
    [item, setItem] = useState("");
  const load = () =>
    api
      .vendors()
      .then(setRows)
      .catch((e) => notify?.(e.message));
  useEffect(() => {
    load();
  }, []);
  const edit = (r) => setForm({ ...r, stitchingItems: r.stitchingItems || [] });
  async function save(e) {
    e.preventDefault();
    try {
      await api.saveVendor(form, form._id);
      notify?.("Vendor saved");
      setForm(blankVendor);
      load();
    } catch (x) {
      notify?.(x.message);
    }
  }
  function addItem() {
    if (item.trim() && !form.stitchingItems.includes(item.trim()))
      setForm({
        ...form,
        stitchingItems: [...form.stitchingItems, item.trim()],
      });
    setItem("");
  }
  return (
    <section className="classic-page">
      <Head
        title="Vendor Registration"
        sub="Register stitching vendors, QC follow-up and all supported items."
        actions={
          <button onClick={() => exportCsv("delivery-vendors.xls", rows)}>
            <Download /> Excel
          </button>
        }
      />
      <div className="classic-card">
        <form onSubmit={save} className="form-grid">
          <label>
            Vendor Code
            <input value={form.vendorCode || "Auto generated"} disabled />
          </label>
          <label>
            Vendor Name
            <input
              required
              value={form.vendorName}
              onChange={(e) => setForm({ ...form, vendorName: e.target.value })}
            />
          </label>
          <label>
            Follow-up QC Name
            <input
              value={form.qcName}
              onChange={(e) => setForm({ ...form, qcName: e.target.value })}
            />
          </label>
          <label className="span-2">
            Address
            <textarea
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
            />
          </label>
          <label className="span-2">
            Stitching Items
            <div className="inline-fields">
              <input
                value={item}
                onChange={(e) => setItem(e.target.value)}
                placeholder="Item name"
              />
              <button type="button" onClick={addItem}>
                <Plus /> Add
              </button>
            </div>
            <div>
              {form.stitchingItems.map((x) => (
                <button
                  type="button"
                  className="tag"
                  key={x}
                  onClick={() =>
                    setForm({
                      ...form,
                      stitchingItems: form.stitchingItems.filter(
                        (y) => y !== x,
                      ),
                    })
                  }
                >
                  {x} ×
                </button>
              ))}
            </div>
          </label>
          <button className="primary" type="submit">
            <Save /> {form._id ? "Update Vendor" : "Save Vendor"}
          </button>
        </form>
      </div>
      <div className="classic-card table-wrap">
        <table>
          <thead>
            <tr>
              <th>S.No</th>
              <th>Code</th>
              <th>Vendor</th>
              <th>Address</th>
              <th>QC</th>
              <th>Stitching Items</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r._id}>
                <td>{i + 1}</td>
                <td>
                  <b>{r.vendorCode}</b>
                </td>
                <td>{r.vendorName}</td>
                <td>{r.address}</td>
                <td>{r.qcName}</td>
                <td>{r.stitchingItems?.join(", ")}</td>
                <td>
                  <button onClick={() => edit(r)}>Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
function Plans({ notify, onPageChange }) {
  const [rows, setRows] = useState([]);
  const load = () =>
    api
      .plans()
      .then(setRows)
      .catch((e) => notify?.(e.message));
  useEffect(() => {
    load();
  }, []);
  return (
    <section className="classic-page">
      <Head
        title="Plan Details"
        sub="Cutting, folding and elastic stage status. Cutting + Folding completion enables section delivery."
        actions={
          <>
            <button onClick={load}>
              <RefreshCw /> Refresh
            </button>
            <button onClick={() => exportCsv("delivery-plan-status.xls", rows)}>
              <Download /> Excel
            </button>
          </>
        }
      />
      <div className="classic-card table-wrap">
        <table>
          <thead>
            <tr>
              <th>S.No</th>
              <th>Plan / DC</th>
              <th>Item</th>
              <th>Cutting</th>
              <th>Folding</th>
              <th>Elastic</th>
              <th>Actual PCS</th>
              <th>Delivered</th>
              <th>Balance</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r._id}>
                <td>{i + 1}</td>
                <td>
                  <b>{r.planNo}</b>
                  <br />
                  <small>{r.dcNo}</small>
                </td>
                <td>{r.itemName}</td>
                <td>{badge(r.cuttingStatus)}</td>
                <td>{badge(r.foldingStatus)}</td>
                <td>{badge(r.elasticStatus)}</td>
                <td>{r.actualPcs}</td>
                <td>{r.deliveredPcs}</td>
                <td>{r.cuttingBalancePcs}</td>
                <td>
                  <button
                    disabled={!r.eligible || !r.cuttingBalancePcs}
                    onClick={() => {
                      sessionStorage.setItem("delivery_plan_no", r.planNo);
                      onPageChange?.("Section Plan");
                    }}
                  >
                    Enter Plan
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
function SectionPlan({ notify }) {
  const [no, setNo] = useState(
      sessionStorage.getItem("delivery_plan_no") || "",
    ),
    [data, setData] = useState(null),
    [vendorCode, setVendorCode] = useState(""),
    [vendor, setVendor] = useState(null),
    [select, setSelect] = useState({ colour: "ALL", size: "ALL", pcs: "ALL" }),
    [challan, setChallan] = useState(null);
  const colours = [...new Set((data?.lines || []).map((x) => x.colour))],
    sizes = [...new Set((data?.lines || []).map((x) => x.size))];
  async function findPlan() {
    try {
      setData(await api.plan(no));
      setChallan(null);
    } catch (e) {
      notify?.(e.message);
    }
  }
  async function findVendor() {
    try {
      setVendor(await api.vendor(vendorCode));
    } catch (e) {
      setVendor(null);
      notify?.(e.message);
    }
  }
  const selected = useMemo(
    () =>
      (data?.lines || [])
        .filter(
          (x) =>
            (select.colour === "ALL" || x.colour === select.colour) &&
            (select.size === "ALL" || x.size === select.size),
        )
        .map((x) => ({
          ...x,
          pcs:
            select.pcs === "ALL"
              ? x.balancePcs
              : Math.min(Number(select.pcs || 0), x.balancePcs),
        }))
        .filter((x) => x.pcs > 0),
    [data, select],
  );
  async function submit() {
    try {
      const row = await api.saveChallan({
        planNo: data.plan.planNo,
        vendorCode: vendor.vendorCode,
        lines: selected,
      });
      setChallan(row);
      notify?.("Delivery challan created; Cutting Stock updated");
      setData(await api.plan(data.plan.planNo));
    } catch (e) {
      notify?.(e.message);
    }
  }
  return (
    <section className="classic-page">
      <Head
        title="Section Plan"
        sub="Select a vendor, colour, size and PCS. ALL sends every available cutting-stock row."
      />
      <div className="classic-card">
        <div className="filter-panel">
          <label>
            Plan / DC No
            <input value={no} onChange={(e) => setNo(e.target.value)} />
          </label>
          <button onClick={findPlan}>
            <Search /> Load Plan
          </button>
        </div>
        {data && (
          <>
            <div className="summary-grid">
              <div>
                <small>Plan / DC</small>
                <b>
                  {data.plan.planNo} / {data.plan.dcNo}
                </b>
              </div>
              <div>
                <small>Item</small>
                <b>{data.plan.itemName}</b>
              </div>
              <div>
                <small>Order / Style</small>
                <b>
                  {data.plan.orderNo} / {data.plan.style}
                </b>
              </div>
              <div>
                <small>Available PCS</small>
                <b>{data.lines.reduce((s, x) => s + x.balancePcs, 0)}</b>
              </div>
            </div>
            {!data.eligible ? (
              <p className="error-text">
                Cutting and Folding must both be completed.
              </p>
            ) : (
              <>
                <div className="form-grid">
                  <label>
                    Vendor Code
                    <div className="inline-fields">
                      <input
                        value={vendorCode}
                        onChange={(e) =>
                          setVendorCode(e.target.value.toUpperCase())
                        }
                      />
                      <button type="button" onClick={findVendor}>
                        <Search />
                      </button>
                    </div>
                  </label>
                  <label>
                    Vendor Name
                    <input disabled value={vendor?.vendorName || ""} />
                  </label>
                  <label>
                    Colour
                    <select
                      value={select.colour}
                      onChange={(e) =>
                        setSelect({ ...select, colour: e.target.value })
                      }
                    >
                      <option>ALL</option>
                      {colours.map((x) => (
                        <option key={x}>{x}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Size
                    <select
                      value={select.size}
                      onChange={(e) =>
                        setSelect({ ...select, size: e.target.value })
                      }
                    >
                      <option>ALL</option>
                      {sizes.map((x) => (
                        <option key={x}>{x}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    PCS
                    <input
                      value={select.pcs}
                      onChange={(e) =>
                        setSelect({
                          ...select,
                          pcs: e.target.value.toUpperCase(),
                        })
                      }
                      placeholder="ALL or number"
                    />
                  </label>
                </div>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Colour</th>
                        <th>Size</th>
                        <th>Actual PCS</th>
                        <th>Already Delivered</th>
                        <th>This Challan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.map((x) => (
                        <tr key={`${x.colour}-${x.size}`}>
                          <td>{x.colour}</td>
                          <td>{x.size}</td>
                          <td>{x.actualPcs}</td>
                          <td>{x.deliveredPcs}</td>
                          <td>
                            <b>{x.pcs}</b>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <th colSpan="4">TOTAL</th>
                        <th>{selected.reduce((s, x) => s + x.pcs, 0)}</th>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <button
                  className="primary"
                  disabled={!vendor || !selected.length}
                  onClick={submit}
                >
                  <Save /> Create Delivery Challan
                </button>
              </>
            )}
          </>
        )}
      </div>
      {challan && <Challan row={challan} />}
    </section>
  );
}
function Challan({ row }) {
  return (
    <div className="classic-card">
      <div className="page-actions">
        <button onClick={() => window.print()}>
          <Printer /> Print
        </button>
        <button
          onClick={() =>
            exportElementExcel(`${row.challanNo}.xls`, "delivery-challan-print")
          }
        >
          <Download /> Excel
        </button>
      </div>
      <div id="delivery-challan-print" className="print-document">
        <h1>DELIVERY CHALLAN</h1>
        <h2>{row.challanNo}</h2>
        <div className="summary-grid">
          <div>
            <small>Date</small>
            <b>{new Date(row.deliveryDate).toLocaleDateString()}</b>
          </div>
          <div>
            <small>Vendor</small>
            <b>
              {row.vendorCode} · {row.vendorName}
            </b>
          </div>
          <div>
            <small>Plan / DC</small>
            <b>
              {row.planNo} / {row.dcNo}
            </b>
          </div>
          <div>
            <small>Item</small>
            <b>{row.itemName}</b>
          </div>
          <div>
            <small>QC</small>
            <b>{row.qcName || "—"}</b>
          </div>
          <div>
            <small>Address</small>
            <b>{row.vendorAddress || "—"}</b>
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th>S.No</th>
              <th>Colour</th>
              <th>Size</th>
              <th>PCS</th>
            </tr>
          </thead>
          <tbody>
            {row.lines.map((x, i) => (
              <tr key={x._id}>
                <td>{i + 1}</td>
                <td>{x.colour}</td>
                <td>{x.size}</td>
                <td>{x.pcs}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th colSpan="3">TOTAL</th>
              <th>{row.totalPcs}</th>
            </tr>
          </tfoot>
        </table>
        <div className="signature-row">
          <span>Prepared By</span>
          <span>Checked By</span>
          <span>Vendor Signature</span>
        </div>
      </div>
    </div>
  );
}
function History({ notify }) {
  const [rows, setRows] = useState([]),
    [f, setF] = useState({ from: "", to: "", vendorCode: "", planNo: "" });
  const load = () =>
    api
      .history(f)
      .then(setRows)
      .catch((e) => notify?.(e.message));
  useEffect(() => {
    load();
  }, []);
  const flat = rows.flatMap((r) =>
    r.lines.map((x) => ({
      Date: new Date(r.deliveryDate).toLocaleDateString(),
      Challan: r.challanNo,
      Vendor: `${r.vendorCode} - ${r.vendorName}`,
      Plan: r.planNo,
      DC: r.dcNo,
      Item: r.itemName,
      Colour: x.colour,
      Size: x.size,
      PCS: x.pcs,
      QC: r.qcName,
    })),
  );
  return (
    <section className="classic-page">
      <Head
        title="Section History"
        sub="Date-wise vendor delivery history with item, colour, size and PCS."
        actions={
          <>
            <button onClick={load}>
              <RefreshCw /> Refresh
            </button>
            <button onClick={() => exportCsv("section-history.xls", flat)}>
              <Download /> Excel
            </button>
            <button onClick={() => window.print()}>
              <Printer /> Print
            </button>
          </>
        }
      />
      <div className="classic-card">
        <div className="filter-panel">
          <label>
            From
            <input
              type="date"
              value={f.from}
              onChange={(e) => setF({ ...f, from: e.target.value })}
            />
          </label>
          <label>
            To
            <input
              type="date"
              value={f.to}
              onChange={(e) => setF({ ...f, to: e.target.value })}
            />
          </label>
          <label>
            Vendor Code
            <input
              value={f.vendorCode}
              onChange={(e) => setF({ ...f, vendorCode: e.target.value })}
            />
          </label>
          <label>
            Plan No
            <input
              value={f.planNo}
              onChange={(e) => setF({ ...f, planNo: e.target.value })}
            />
          </label>
          <button onClick={load}>
            <Search /> Filter
          </button>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Challan</th>
                <th>Vendor</th>
                <th>Plan / DC</th>
                <th>Item</th>
                <th>Colour</th>
                <th>Size</th>
                <th>PCS</th>
              </tr>
            </thead>
            <tbody>
              {flat.map((x, i) => (
                <tr key={i}>
                  <td>{x.Date}</td>
                  <td>{x.Challan}</td>
                  <td>{x.Vendor}</td>
                  <td>
                    {x.Plan} / {x.DC}
                  </td>
                  <td>{x.Item}</td>
                  <td>{x.Colour}</td>
                  <td>{x.Size}</td>
                  <td>{x.PCS}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
